<script lang="ts" setup>
import type { ContentArticle } from '~/models/Content/ContentArticle'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import useAuthStore from '~/stores/auth'
import { ColorTheme } from '~/models/Content/Color'
import { useColorTheme } from '~/composables/useColorTheme'
import Button from '~/components/ui/Button.vue'
import TextToSpeechButton from '~/components/media/TextToSpeechButton.vue'
import ArticleTeacherGuide from '~/components/article/ArticleTeacherGuide.vue'
import ArticleStudentGuide from '~/components/article/ArticleStudentGuide.vue'

const { article } = defineProps<{ article: ContentArticle }>()

const { t } = useI18n()
const { isTeacher } = storeToRefs(useAuthStore())
const { theme } = useColorTheme(article.colorTheme.identifier ?? ColorTheme.BlueOrange)

const isTeacherGuideOpen = ref(false)
const isStudentGuideOpen = ref(false)

const hasTeacherGuide = computed(() => isTeacher.value && Number(article.teacherContent?.destinationContentId) > 0)
const hasStudentGuide = computed(() => Number(article.studentGuide?.length) > 0)
const hasGuides = computed(() => hasTeacherGuide.value || hasStudentGuide.value)
const showBar = computed(() => hasGuides.value || article.textToSpeech)

const needsTopMargin = computed(() => article.headerVariation?.identifier === 'text_and_image')
</script>

<template>
  <div
    v-if="showBar"
    :class="{ 'mt-6': needsTopMargin }"
    class="xs:flex-wrap flex flex-nowrap items-center gap-2"
  >
    <Button
      v-if="isTeacher && hasTeacherGuide"
      color="teacher"
      size="lg"
      @click="isTeacherGuideOpen = true"
    >
      {{ t('article.toTeacher') }}
    </Button>
    <Button
      v-if="hasStudentGuide"
      :color="theme[60]"
      size="lg"
      class="bg-(--theme-5) text-(--theme-50) outline-(--theme-50) hover:text-(--theme-60) hover:not-disabled:bg-(--theme-10)"
      variant="outline"
      @click="isStudentGuideOpen = true"
    >
      {{ t('article.toStudent') }}
    </Button>
    <TextToSpeechButton
      v-if="article.textToSpeech && article.textToSpeechLanguage.identifier"
      :item="article"
      :language-code="article.textToSpeechLanguage.identifier"
      selector=".au-article"
    />
    <ArticleStudentGuide
      v-if="hasStudentGuide"
      :content="article.studentGuide"
      :is-open="isStudentGuideOpen"
      @close-guide="isStudentGuideOpen = false"
    />
    <ArticleTeacherGuide
      v-if="isTeacher && hasTeacherGuide"
      :content-id="article.teacherContent!.destinationContentId"
      :is-open="isTeacherGuideOpen"
      @close-guide="isTeacherGuideOpen = false"
    />
  </div>
</template>
